/* eslint @typescript-eslint/sort-type-constituents: "error" */

import type { BaseNodeCfg, FlowCfg, LatestMaterialComparisonCfg } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import type { FlowEventEmitter } from '@redviking/argonaut-core-ui/src/applet/design/flows/editor-composition/useFlowEvents';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import type { Component } from 'vue';
import type { FunctionalSelectItem } from '@redviking/argonaut-util/types/mes/applet-designs/latest/flow/nodes/functional-select.zod';
import type { MaterialModelNodeControlData } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/SelectMaterialModelControl.vue';
import type { FisDataLabelFragment } from '@redviking/argonaut-core-ui/types/db-ops';
import type { ArgoConnectionKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/connection_kind';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WideControlEventHandler = (payload: any, ctx: {
    nodeCfg: BaseNodeCfg,
    flowEventEmitter: FlowEventEmitter,
    flowCfg: FlowCfg,
    controlKey: string,
    target: 'controlValue' | 'input' | 'output',
}) => (Promise<void> | void);

type ControlEventHandler<T, NODE_CFG extends BaseNodeCfg> = (payload: T, ctx: {
    nodeCfg: NODE_CFG,
    flowEventEmitter: FlowEventEmitter,
    flowCfg: FlowCfg,
    controlKey: string,
    target: 'controlValue' | 'input' | 'output',
}) => (Promise<void> | void);

/** defines the configuration options and events for controls */
export namespace FlowNodeControl {
    
    export type Kind =
        | 'asset'
        | 'boolean'
        | 'button'
        | 'connectionSelect'
        | 'createMaterialAttributes'
        | 'deletableSelection'
        | 'deletableSocket'
        | 'deletableTextControlWithTypeSelector'
        | 'functionalFlowSelect'
        | 'location'
        | 'materialComparisonCfg'
        | 'materialModelSelect'
        | 'number'
        | 'performanceDataCollectDataLabelId'
        | 'scriptSelect'
        | 'selectEvent'
        | 'selection'
        | 'selectProcess'
        | 'selectTabGroup'
        | 'selectVar'
        | 'selectWithCallback'
        | 'selectWorkInstructionVersion'
        | 'text'
        | 'textarea'
        | 'varValue';
    
    export namespace Configs {
        /** when you want a control cfg but don't care which */
        export interface WideCfg {
            kind: string;
            label?: string;
            /**
             * The flag for optional determines styling for the label using italics for optional fields
             *
             * In order to get the same styling on the labels for the sockets we need to make our own socket components which I think is in scope eventually
             * NOTE: We _could_ make a custom label component and set the default socket text to an empty string, but I feel like that is a bandaid
             */
            optional?: boolean;

        }

        export interface Base<K extends Kind> extends WideCfg {
            kind: K;
        }

        export interface AssetControlCfg extends Base<'asset'> {
            label: string;
        }
        
        export interface BooleanControlCfg extends Base<'boolean'> {
            tooltipText?: string;
        }

        export interface ButtonControlCfg extends Base<'button'> {
            label: string;
            useDialog: {
                contentComponent: () => Promise<Component>;
            } | null;
        }

        export interface ConnectionSelectControlCfg extends Base<'connectionSelect'> {
            /** the connection kinds selectable by this control, used to filter the list of available connections */
            allowedConnectionKinds: ArgoConnectionKindEnum[];
        }

        export interface CreateMaterialAttributesControlCfg extends Base<'createMaterialAttributes'> { placeholder?: string }

        export interface DeletableSelectionControlCfg extends Base<'deletableSelection'> {
            label: string;
            /** @deprecated this should be removed as it is only used by the io-link, which is now suppressed */
            onDelete: () => void;
            placeholder?: string;
            items: Selection.SelectionItem[] | (() => Selection.SelectionItem[]);
        }

        export interface DeletableSocketControlCfg extends Base<'deletableSocket'> {
            childControl?: FlowNodeControl.WideOpts;
            label?: string;
            rightAlign?: boolean;
        }
        
        export interface DeletableTextWithTypeSelectorControlCfg extends Base<'deletableTextControlWithTypeSelector'> {
            valuePlaceholder?: string;
            selectionPlaceholder?: string;
            /** specify a subset of socket data types allowed for this connection */
            allowedDataTypes: FunctionalSelectItem['dataType'][];
            kind: 'deletableTextControlWithTypeSelector';
        }

        export interface FunctionalFlowSelectControlCfg extends Base<'functionalFlowSelect'> {
            placeholder?: string;
        }

        export interface LocationControlCfg extends Base<'location'> {}

        export interface MaterialComparisonCfgControl extends Base<'materialComparisonCfg'> {}

        export interface MaterialModelIdControlCfg extends Base<'materialModelSelect'> {
            placeholder?: string;
        }

        export interface NumberControlCfg extends Base<'number'> {
            min?: number;
            max?: number;
        }

        export interface PerformanceDataLabelIdControlCfg extends Base<'performanceDataCollectDataLabelId'> {
            placeholder?: string;
        }

        export namespace Selection {
            export interface Cfg extends Base<'selection'> {
                placeholder?: string;
                items: SelectionItem[] | (() => SelectionItem[]);
            }

            export type SelectionItem = {
                label: string;
                tooltip?: string;
                value: unknown;
            };
            export type SelectionItemValue = SelectionItem['value'];
        }

        export interface SelectProcessControlCfg extends Base<'selectProcess'> {
        }

        export interface SelectTabGroupControlCfg extends Base<'selectTabGroup'> {
            // controls if the sync button is shown on the control. Useful for when we want users to manually be able to refetch the tab group config
            useSync: boolean;
        }

        export interface SelectVarControlCfg extends Base<'selectVar'> {
            /** allowed variable types */
            allowedDataTypes?: ('bool' | 'num' | 'str')[];
        }

        /**
         * @deprecated I think this has parity with the selection control, and could be removed.
         */
        export interface SelectionControlWithCallbackControlCfg extends Base<'selectWithCallback'> {
            selectLabel: string;
            items: Selection.SelectionItem[] | (() => Promise<Selection.SelectionItem[]> | Selection.SelectionItem[]);
        }

        export interface SelectWorkInstructionVersionControlCfg extends Base<'selectWorkInstructionVersion'> {
        }

        export interface TextControlCfg extends Base<'text'> { placeholder?: string }

        export interface TextareaControlCfg extends Base<'textarea'> {
            placeholder?: string;
        }

        export interface VarValueControlCfg extends Base<'varValue'> {
            /** if set, the control will only allow values of the specified type to be selected */
            dataType?: 'bool' | 'num' | 'str';
        }

        export interface ConfigMap extends ValidateControlConfigMap<{
            asset: AssetControlCfg;
            boolean: BooleanControlCfg;
            button: ButtonControlCfg;
            connectionSelect: ConnectionSelectControlCfg;
            createMaterialAttributes: CreateMaterialAttributesControlCfg;
            deletableSelection: DeletableSelectionControlCfg;
            deletableSocket: DeletableSocketControlCfg;
            deletableTextControlWithTypeSelector: DeletableTextWithTypeSelectorControlCfg;
            functionalFlowSelect: FunctionalFlowSelectControlCfg;
            location: LocationControlCfg;
            materialComparisonCfg: MaterialComparisonCfgControl;
            materialModelSelect: MaterialModelIdControlCfg;
            number: NumberControlCfg;
            performanceDataCollectDataLabelId: PerformanceDataLabelIdControlCfg;
            scriptSelect: Base<'scriptSelect'>;
            selectEvent: Base<'selectEvent'>;
            selection: Selection.Cfg;
            selectProcess: SelectProcessControlCfg;
            selectTabGroup: SelectTabGroupControlCfg;
            selectVar: SelectVarControlCfg;
            selectWithCallback: SelectionControlWithCallbackControlCfg;
            selectWorkInstructionVersion: SelectWorkInstructionVersionControlCfg;
            text: TextControlCfg;
            textarea: TextareaControlCfg;
            varValue: VarValueControlCfg;
        }> {}

        /**
         * used to ensure all control config types are correct in `FlowNodeControl.ConfigMap`
         */
        type ValidateControlConfigMap<T extends {
            [CONTROL_KIND in FlowNodeControl.Kind]: Configs.Base<CONTROL_KIND>;
        }> = T;
    }

    export type Config<K extends Kind> = Configs.ConfigMap[K] & { kind: K };

    /** value types managed/emitted by controls */
    export namespace Values {

        /**
         * values that a control emits on events. `update:value` is required even if its void
         */
        export interface EventValueMap extends ValidateEventMap<{
            asset: { 'update:value': Latest.AssetCfg };
            boolean: { 'update:value': boolean };
            button: { click: unknown };
            connectionSelect: { 'update:value': string };
            createMaterialAttributes: { 'update:value': string };
            deletableSelection: { 'update:value': unknown };
            deletableSocket: { delete: void };
            deletableTextControlWithTypeSelector: {
                delete: void;
                'update:value': { newValue: string; dataType: FunctionalSelectItem['dataType'] };
            };
            functionalFlowSelect: { 'update:value': string | null };
            location: { 'update:value': Latest.LocationCfg | undefined };
            materialComparisonCfg: { 'update:value': LatestMaterialComparisonCfg };
            materialModelSelect: {
                'update:value': string;
                select: MaterialModelNodeControlData;
            };
            number: { 'update:value': number };
            performanceDataCollectDataLabelId: {
                'update:value': string;
                select: FisDataLabelFragment;
            };
            scriptSelect: { 'update:value': string };
            selectEvent: { 'update:value': string };
            selectProcess: { 'update:value': string };
            selectTabGroup: { 'update:value': string };
            selectVar: { 'update:value': string };
            selectWithCallback: {
                'update:value': unknown;
                select: unknown;
            };
            selectWorkInstructionVersion: { 'update:value': string };
            selection: {
                'update:value': unknown;
                'update:search': string;
                click: void;
                enter: void;
                'append-click': void;
            };
            text: { 'update:value': string };
            textarea: { 'update:value': string };
            varValue: { 'update:value': Latest.AppletVariables.AppletVariableValue };
        }> {}

        type ValidateEventMap<T extends {
            [CONTROL_KIND in FlowNodeControl.Kind]: ValueMapItem
        }> = T;

        type ValueMapItem = {
            [eventName: string]: unknown;
        }

        export type ComponentEmitSignature<K extends Kind> = {
            [EVENT_NAME in keyof EventValueMap[K]]: (payload: EventValueMap[K][EVENT_NAME]) => void;
        }

        export type EventName<K extends Kind> = K extends Kind ? keyof EventValueMap[K] : never;

        export type EventValue<K extends Kind, EVENT_NAME extends EventName<K>> = K extends Kind
            ? EVENT_NAME extends keyof EventValueMap[K]
                ? EventValueMap[K][EVENT_NAME]
                : never
            : never;
    }
    
    export type Value<K extends Kind> = 'update:value' extends keyof Values.EventValueMap[K] ? Values.EventValueMap[K]['update:value'] : never;

    /** for when you want control opts but don't care which kind */
    export interface WideOpts<T = unknown> {
        cfg: FlowNodeControl.Configs.WideCfg,
        value: T,
        index?: number,
        on: {
            'update:value': WideControlEventHandler;
        } & {
            [EVENT_NAME: string]: WideControlEventHandler | undefined;
        },
        /**
         * @default false
         *
         * the default behavior for a socket is to disable the default value control if the socket is wired, set this to true to bypass that and the default value control won't be disabled
         */
        enableWithConnectedSocket?: boolean,
    }
    
    export interface Opts<K extends Kind, T> {
        cfg: FlowNodeControl.Config<K>,
        value: T,
        index?: number,
        on: {
            /** required handler */
            'update:value': ControlEventHandler<T, BaseNodeCfg>
        } & {
            // FUTURE to avoid bugs, we might want to force specifying a `update:value` handler instead of making it optional.
            // Additionally we might want to define a type that maps every control to its allowed value type.
            [EVENT_NAME in FlowNodeControl.Values.EventName<K>]?: ControlEventHandler<FlowNodeControl.Values.EventValue<K, EVENT_NAME>, BaseNodeCfg>
        };
        /**
         * @default false
         *
         * the default behavior for a socket is to disable the default value control if the socket is wired, set this to true to bypass that and the default value control won't be disabled
         */
        enableWithConnectedSocket?: boolean,
    }
}

/**
 * type helper
 */
export function defineControlOpts<K extends FlowNodeControl.Kind, T> (opts: FlowNodeControl.Opts<K, T>): FlowNodeControl.WideOpts<T> {
    return opts as FlowNodeControl.WideOpts<T>;
}
